// src/pages/SignUp.jsx
// Members, create-account page.
// A controlled username, email, and password form that registers through the
// AuthProvider. Registration returns a token, so a successful signup logs the
// user in and sends them to the home page.

import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";

export default function SignUp() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await register({ username, email, password });
      navigate("/", { replace: true });
    } catch (err) {
      // The backend returns { success: false, error: "..." } for a taken
      // username or email, or a validation failure.
      setError(
        err.response?.data?.error ||
          "Unable to create your account. Please try again.",
      );
      setSubmitting(false);
    }
  }

  const inputClasses =
    "w-full bg-transparent border-0 border-b border-ink-20 py-3 text-body text-ink focus:outline-none focus:border-ink transition-colors";

  return (
    <>
      {/* Page header */}
      <section className="max-w-wide mx-auto px-6 pt-20 pb-12 md:pt-28 md:pb-16">
        <p className="label-mono">Members</p>
        <h1 className="font-display text-h2 leading-tight font-medium mt-3">
          Create Account
        </h1>
        <p className="text-body text-ink-70 leading-relaxed mt-6 max-w-2xl">
          Register to follow along. Pick a username, an email, and a password of
          at least eight characters.
        </p>
      </section>

      <hr className="hairline max-w-wide mx-auto" />

      {/* Form */}
      <section className="max-w-wide mx-auto px-6 py-20 md:py-28">
        <div className="max-w-md">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <label htmlFor="username" className="label-mono block mb-2">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                minLength={3}
                maxLength={50}
                autoComplete="username"
                className={inputClasses}
              />
            </div>

            <div>
              <label htmlFor="email" className="label-mono block mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className={inputClasses}
              />
            </div>

            <div>
              <label htmlFor="password" className="label-mono block mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className={inputClasses}
              />
            </div>

            {error && <p className="text-small text-oxblood">{error}</p>}

            <div className="pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="label-mono border-b border-ink hover:text-oxblood hover:border-oxblood transition-colors py-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-ink disabled:hover:border-ink"
              >
                {submitting ? "Creating account..." : "Create account →"}
              </button>
            </div>
          </form>

          <p className="text-small text-ink-70 mt-10">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-ink border-b border-ink-20 hover:text-oxblood hover:border-oxblood transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </section>
    </>
  );
}
